import React, { useState, useEffect } from 'react';
import BookingModal from '../components/BookingModal';
import CarCard from '../components/CarCard';
import tiranaImg from '../tirana.jpg';
import tiranaVideo from '../tirana.mp4';
import fabiaSvg from '../fabia.jpg';

const LOCATIONS = [
  { value: 'tirana_center', label: 'Tirana City Center' },
  { value: 'tirana_airport', label: "Tirana Int'l Airport (TIA)" },
];

export default function Home({ setPage }) {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [search, setSearch] = useState({ pickup: 'tirana_center', dropoff: 'tirana_center', pickupDate: '', returnDate: '' });
  const [searchError, setSearchError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetch('/api/cars?available=true')
      .then(r => r.json())
      .then(data => setFeaturedCars(data.slice(0, 3)))
      .catch(() => {});
  }, []);

  const handleSearch = () => {
    if (!search.pickupDate || !search.returnDate) { setSearchError('Please select both pickup and return dates.'); return; }
    if (new Date(search.returnDate) <= new Date(search.pickupDate)) { setSearchError('Return date must be after pickup date.'); return; }
    setSearchError('');
    setPage('cars');
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      {/* HERO */}
      <section style={{ position: 'relative', minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <video autoPlay loop muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 25%', zIndex: 0 }}>
          <source src={tiranaVideo} type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(105deg, rgba(10,10,10,0.82) 0%, rgba(10,10,10,0.65) 45%, rgba(10,10,10,0.25) 75%, rgba(10,10,10,0.1) 100%)' }} />
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: 'var(--red)', zIndex: 2 }} />

        <div style={{ position: 'relative', zIndex: 3, flex: 1, display: 'flex', alignItems: 'center', maxWidth: 1240, margin: '0 auto', padding: isMobile ? '40px 16px' : '60px 24px', width: '100%', gap: isMobile ? 0 : 60 }}>
          {/* LEFT — Text */}
          <div style={{ flex: '0 0 auto', maxWidth: isMobile ? '100%' : 520, width: isMobile ? '100%' : 'auto' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(196,18,48,0.15)', border: '1px solid rgba(196,18,48,0.4)', padding: '6px 16px', borderRadius: 20, marginBottom: 28, fontSize: 11, fontWeight: 700, color: '#ff6b7a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--red)', animation: 'pulse-dot 2s infinite', display: 'inline-block' }} />
              Tirana's Premier Car Rental
            </div>

            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 'clamp(52px, 14vw, 80px)' : 'clamp(64px, 8vw, 108px)', lineHeight: 0.92, letterSpacing: '0.02em', color: '#fff', marginBottom: 20 }}>
              DRIVE<br />
              <span style={{ color: 'var(--red)' }}>BEYOND</span><br />
              LIMITS
            </h1>

            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.75, marginBottom: 36, maxWidth: 420, fontWeight: 300 }}>
              Explore Albania in style. Pick up in Tirana city or straight from the airport — no hidden fees, instant confirmation.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 48 }}>
              <button className="btn-primary" style={{ padding: '14px 32px', fontSize: 15, flex: isMobile ? 1 : 'none' }} onClick={() => setPage('cars')}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                Browse Fleet
              </button>
              <button onClick={() => setPage('cars')} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', padding: '14px 28px', borderRadius: 'var(--radius)', fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'var(--transition)', flex: isMobile ? 1 : 'none', textAlign: 'center' }}>
                View All Cars
              </button>
            </div>

            <div style={{ display: 'flex', gap: 36 }}>
              {[['200+', 'Vehicles'], ['15K', 'Trips Done'], ['4.9★', 'Rating']].map(([num, label]) => (
                <div key={label}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 34, letterSpacing: '0.04em', color: '#fff', lineHeight: 1 }}>{num}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Featured Car Card (hidden on mobile) */}
          {!isMobile && (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <div style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Featured Car</span>
                  <span className="badge badge-green">Available</span>
                </div>
                <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 20, background: '#f0f0f0' }}>
                  <img src={fabiaSvg} alt="Skoda Fabia 2026" style={{ width: '100%', height: 180, objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 20, color: '#fff', marginBottom: 2 }}>Škoda Fabia</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>2026 · Hatchback · Automatic</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: 'var(--red)', letterSpacing: '0.04em', lineHeight: 1 }}>€20</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>per day</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 0, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16, marginBottom: 20 }}>
                  {[['5', 'Seats'], ['Manual', 'Trans.'], ['Petrol', 'Fuel']].map(([val, lbl]) => (
                    <div key={lbl} style={{ flex: 1, textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{val}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{lbl}</div>
                    </div>
                  ))}
                </div>
                <button className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '13px' }} onClick={() => featuredCars[0] && setSelectedCar(featuredCars[0])}>
                  Book This Car — €{featuredCars[0]?.price || 30}/day
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* BOOKING ENGINE */}
      <section style={{ background: 'var(--bg)', paddingBottom: '60px', position: 'relative', zIndex: 10 }}>
      <div className="booking-engine-card" style={{ padding: '0 24px' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', gap: 0 }}>
            <div style={{ padding: '18px 28px', fontSize: 13, fontWeight: 700, color: 'var(--red)', borderBottom: '2px solid var(--red)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              Reserve a Car
            </div>
          </div>

          {/* Fields */}
          <div style={{ padding: '20px 0', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr auto', gap: isMobile ? 12 : 0, alignItems: 'end' }}>
            <BookingField icon={<PinIcon />} label="Pickup Location" divider={!isMobile}>
              <select className="booking-field-input" style={{ fontSize: 16 }} value={search.pickup} onChange={e => setSearch({ ...search, pickup: e.target.value })}>
                {LOCATIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </BookingField>

            <BookingField icon={<PinIcon />} label="Drop-off Location" divider={!isMobile}>
              <select className="booking-field-input" style={{ fontSize: 16 }} value={search.dropoff} onChange={e => setSearch({ ...search, dropoff: e.target.value })}>
                {LOCATIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </BookingField>

            <BookingField icon={<CalIcon />} label="Pickup Date" divider={!isMobile}>
              <input type="date" className="booking-field-input" style={{ fontSize: 16 }} value={search.pickupDate} min={today}
                onChange={e => setSearch({ ...search, pickupDate: e.target.value })} />
            </BookingField>

            <BookingField icon={<CalIcon />} label="Return Date" divider={false}>
              <input type="date" className="booking-field-input" style={{ fontSize: 16 }} value={search.returnDate}
                min={search.pickupDate || today}
                onChange={e => setSearch({ ...search, returnDate: e.target.value })} />
            </BookingField>

            <div style={{ padding: isMobile ? '4px 0 0 0' : '0 0 0 20px' }}>
              <button onClick={handleSearch} style={{
                background: 'var(--red)', color: '#fff', border: 'none',
                borderRadius: 10, padding: '16px 28px', fontFamily: 'var(--font-body)',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                transition: 'var(--transition)', whiteSpace: 'nowrap',
                boxShadow: '0 4px 20px rgba(196,18,48,0.35)',
                width: isMobile ? '100%' : 'auto'
              }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                Search
              </button>
            </div>
          </div>

          {searchError && (
            <div style={{ paddingBottom: 16, color: 'var(--red)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
              {searchError}
            </div>
          )}

          <div style={{ paddingBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { emoji: '📍', text: 'Tirana City Center' },
              { emoji: '✈️', text: 'TIA Airport Pickup' },
              { emoji: '🛡️', text: 'Free cancellation' },
              { emoji: '💳', text: 'No hidden fees' },
            ].map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F7F7F7', border: '1px solid #EBEBEB', borderRadius: 20, padding: '5px 12px', fontSize: 12, color: '#666', fontWeight: 500 }}>
                {p.emoji} {p.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT US */}
      <section style={{ background: '#fff', padding: isMobile ? '60px 0' : '100px 0', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 48 : 80, alignItems: 'center' }}>

            {/* LEFT — Image */}
            <div style={{ position: 'relative', paddingBottom: isMobile ? 0 : 0 }}>
              <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.12)' }}>
                <img src={tiranaImg} alt="Tirana Albania" style={{ width: '100%', height: isMobile ? 260 : 460, objectFit: 'cover', objectPosition: 'center 30%', display: 'block' }} />
                <div style={{ position: 'absolute', inset: 0, borderRadius: 16, background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)' }} />
              </div>
              <div style={{ position: 'absolute', top: 32, left: -6, width: 4, height: 80, background: 'var(--red)', borderRadius: 2 }} />

              {/* Floating stat card */}
              {!isMobile && (
                <div style={{ position: 'absolute', bottom: -24, left: -24, background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 12px 40px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.06)', minWidth: 160 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, color: 'var(--red)', letterSpacing: '0.04em', lineHeight: 1 }}>14+</div>
                  <div style={{ fontSize: 12, color: '#999', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4, fontWeight: 600 }}>Years of Service</div>
                </div>
              )}

              {/* TIA badge */}
              <div style={{ position: 'absolute', top: isMobile ? 12 : -16, right: isMobile ? 12 : -16, background: 'var(--red)', borderRadius: 12, padding: '14px 20px', boxShadow: '0 8px 28px rgba(196,18,48,0.35)', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#fff', letterSpacing: '0.04em', lineHeight: 1 }}>TIA</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 3, fontWeight: 600 }}>Airport</div>
              </div>
            </div>

            {/* RIGHT — Text */}
            <div style={{ marginTop: isMobile ? 16 : 0 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(196,18,48,0.06)', border: '1px solid rgba(196,18,48,0.2)', padding: '6px 16px', borderRadius: 20, marginBottom: 24, fontSize: 11, fontWeight: 700, color: 'var(--red)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                About Rent355
              </div>

              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 4vw, 58px)', letterSpacing: '0.03em', color: '#1A1A1A', lineHeight: 0.95, marginBottom: 24 }}>
                TIRANA'S MOST<br />TRUSTED <span style={{ color: 'var(--red)' }}>RENTAL</span>
              </h2>

              <p style={{ fontSize: 15, color: '#666', lineHeight: 1.8, marginBottom: 16, fontWeight: 300 }}>
                For over <strong style={{ color: '#1A1A1A', fontWeight: 700 }}>14 years</strong>, Rent355 has been Albania's premier car rental service — rooted in Tirana, trusted by thousands of travelers from around the world.
              </p>
              <p style={{ fontSize: 15, color: '#666', lineHeight: 1.8, marginBottom: 36, fontWeight: 300 }}>
                Whether you arrive at <strong style={{ color: '#1A1A1A', fontWeight: 700 }}>Tirana International Airport</strong> or need a vehicle in the city center, our team is there — ready to welcome you with a smile and the keys to your perfect car.
              </p>

              <div style={{ marginBottom: 36 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#999', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>We Speak Your Language</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[{ flag: '🇬🇧', lang: 'English' }, { flag: '🇮🇹', lang: 'Italian' }, { flag: '🇩🇪', lang: 'German' }, { flag: '🇫🇷', lang: 'French' }].map(({ flag, lang }) => (
                    <div key={lang} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F7F7F7', border: '1px solid #E8E8E8', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, color: '#333' }}>
                      <span style={{ fontSize: 18 }}>{flag}</span> {lang}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 0, borderTop: '1px solid #EBEBEB', paddingTop: 32 }}>
                {[{ value: '14+', label: 'Years Experience' }, { value: '200+', label: 'Vehicles' }, { value: '15K+', label: 'Happy Clients' }, { value: '4.9★', label: 'Average Rating' }].map((s, i) => (
                  <div key={s.label} style={{ flex: 1, textAlign: 'center', borderRight: i < 3 ? '1px solid #EBEBEB' : 'none', padding: '0 8px' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 30, color: 'var(--red)', letterSpacing: '0.04em', lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
                    <div style={{ fontSize: isMobile ? 9 : 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section style={{ background: '#0C0C0C', padding: isMobile ? '60px 0' : '88px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, background: 'radial-gradient(circle, rgba(196,18,48,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(196,18,48,0.12)', border: '1px solid rgba(196,18,48,0.25)', padding: '6px 16px', borderRadius: 20, marginBottom: 18, fontSize: 11, fontWeight: 700, color: '#ff6b7a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Why Rent355
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px,4vw,56px)', letterSpacing: '0.04em', color: '#fff', lineHeight: 1 }}>
              WHY CHOOSE <span style={{ color: 'var(--red)' }}>US</span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 15, marginTop: 12, maxWidth: 480, margin: '12px auto 0', lineHeight: 1.7 }}>
              Over 14 years of trusted service in Albania — here's what sets us apart.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 2 }}>
            {[
              { icon: <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/></svg>, num: '01', title: 'Fully Insured', desc: 'Every vehicle in our fleet comes with comprehensive insurance — you drive with total confidence and zero worries.', accent: '#C41230' },
              { icon: <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802"/></svg>, num: '02', title: 'Multilingual Team', desc: 'Our staff speaks English, Italian, German and French — we make every international guest feel right at home.', accent: '#2563EB' },
              { icon: <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>, num: '03', title: 'Airport Pickup', desc: "Land at Tirana International Airport and we'll be waiting for you — keys in hand, no delays, no extra cost.", accent: '#059669' },
              { icon: <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>, num: '04', title: '24/7 Support', desc: 'Day or night, our team is always reachable by phone or WhatsApp — we never leave you stranded on the road.', accent: '#D97706' },
              { icon: <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/></svg>, num: '05', title: 'Best Price Guarantee', desc: "Transparent pricing with no hidden fees. Starting from just €20/day — premium service that doesn't break the bank.", accent: '#7C3AED' },
              { icon: <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/></svg>, num: '06', title: '14+ Years Experience', desc: 'Since 2010, thousands of locals and tourists have trusted Rent355. Our reputation is built on every single satisfied client.', accent: '#C41230' },
            ].map((item, i) => (
              <div key={i} style={{ background: '#141414', padding: '36px 32px', borderTop: `3px solid ${item.accent}`, transition: 'all 0.25s', cursor: 'default', position: 'relative', overflow: 'hidden' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1a1a1a'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#141414'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ position: 'absolute', top: 16, right: 20, fontFamily: 'var(--font-display)', fontSize: 56, color: 'rgba(255,255,255,0.04)', letterSpacing: '0.04em', lineHeight: 1, userSelect: 'none' }}>{item.num}</div>
                <div style={{ width: 52, height: 52, background: `${item.accent}18`, border: `1px solid ${item.accent}30`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.accent, marginBottom: 20 }}>
                  {item.icon}
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 10, letterSpacing: '0.01em' }}>{item.title}</div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.75, fontWeight: 300 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED FLEET */}
      <section className="cars-section" style={{ background: 'var(--bg)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">FEATURED<br /><span>FLEET</span></h2>
            <button className="btn-outline" onClick={() => setPage('cars')}>View All →</button>
          </div>
          <div className="cars-grid">
            {featuredCars.map(car => (
              <CarCard key={car.id} car={car} onBook={setSelectedCar} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0', borderTop: '1px solid var(--border)', background: '#fff' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(40px,5vw,72px)', letterSpacing: '0.04em', color: 'var(--text)', marginBottom: 16, lineHeight: 1 }}>
            READY TO <span style={{ color: 'var(--red)' }}>ROLL?</span>
          </div>
          <p style={{ color: 'var(--text-dim)', fontSize: 16, maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.7 }}>
            Join thousands of satisfied customers who trust Rent355 for their Albanian adventure.
          </p>
          <button className="btn-primary" style={{ padding: '16px 40px', fontSize: 16 }} onClick={() => setPage('cars')}>
            Book Your Car Today
          </button>
        </div>
      </section>

      {selectedCar && (
        <BookingModal car={selectedCar} onClose={() => setSelectedCar(null)} onSuccess={() => setSelectedCar(null)} />
      )}
    </>
  );
}

function BookingField({ icon, label, children, divider }) {
  return (
    <div style={{ padding: '0 24px 0 0', borderRight: divider ? '1px solid #EBEBEB' : 'none', paddingRight: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color: '#999', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
        {icon} {label}
      </div>
      {children}
    </div>
  );
}

const PinIcon = () => <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>;
const CalIcon = () => <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>;